import React, {
  ChangeEventHandler,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { InputProps } from '@chakra-ui/input';
import { AiOutlineDownload as Download, AiFillFolderAdd as Folder } from 'react-icons/ai';

import { Container, InputFile, InputArea, FileItem } from './styles';

import { Label } from '../';
import { Icon } from 'components/Icon';
import { openNotification } from 'utils/notification';

interface IProps extends InputProps {
  label?: string;
  placeholderType?: string;
  inputRef?: React.RefObject<IFileField>;
  maxSize?: number;
  types?: string[];
}

export interface IFileField {
  files: Array<File>;
  inputRef: HTMLInputElement | null;
  value: IFileField['files'];
}

const FileFieldComponent: React.FC<IProps> = ({
  inputRef,
  label,
  onChange,
  placeholderType,
  types,
  ...props
}) => {
  const lRef = useRef<HTMLLabelElement | null>(null);
  const [fSize, setFSize] = useState(0);

  const filesRef = useRef<IFileField>({
    files: [],
    inputRef: null,
    get value() {
      return this.files;
    },
    set value(files: IFileField['files']) {
      this.files = files;
      setFSize(this.files.length);
    },
  });

  useEffect(() => {
    // @ts-ignore
    if (inputRef) inputRef.current = filesRef.current;
  }, []);

  const handleChange = useCallback<ChangeEventHandler<HTMLInputElement>>(
    e => {
      if (onChange) onChange(e);
      if (e.target.files) {
        filesRef.current.value = [
          ...filesRef.current.value,
          ...Array.from(e.target.files).filter(i => {
            if (types && !i.name.match(new RegExp(`.(${types.join('|')})+$`, 'i'))) {
              openNotification('Tipo de arquivo não aceito', 'warn');
              return false;
            } else if (i.size <= 0xa00000) return true;
            else {
              openNotification('Tamanho máximo de 10mb por arquivo', 'warn');
              return false;
            }
          }),
        ];
      }
      e.target.setAttribute('value', '');
      e.target.value = '';
      setFSize(filesRef.current.files.length);
    },
    [onChange],
  );

  const nProps = useMemo(() => ({ ...props, ...{ id: props.id || props.name || label } }), [props]);

  const files = useMemo(() => {
    if (fSize > 0) return filesRef.current.value;
    return [];
  }, [fSize]);

  const handleRemoveFile = useCallback((index: number) => {
    filesRef.current.value = filesRef.current.value.filter((i, k) => k !== index);
  }, []);

  return (
    <Container>
      {label && (
        <Label ref={lRef} htmlFor={props.id || props.name || label}>
          {label}
        </Label>
      )}
      <InputArea onClick={() => lRef.current?.click()}>
        <Folder fontSize={'2.5rem'} color={'#1b1b1b'} />
        {!!props.placeholder && <p>{props.placeholder}</p>}
        {!!placeholderType && <p>{placeholderType}</p>}
        <InputFile
          onChange={handleChange}
          type="file"
          multiple
          ref={(ref: IFileField['inputRef']) => (filesRef.current.inputRef = ref)}
          {...nProps}
        />
      </InputArea>
      {files.map((i, k) => (
        <FileItem key={k}>
          <a
            href={URL.createObjectURL(i)}
            download={i.name}
            rel="noopener noreferrer"
            target="_blank"
          >
            <p style={{ color: '#0094FF', fontSize: '14px' }}>
              <span>
                <Download color={'#0094FF'} fontSize={'25px'} />
              </span>
              {i.name}
            </p>
          </a>
          <button type="button" onClick={handleRemoveFile.bind(null, k)}>
            <Icon name="trashed" size={1} color={'red200'} />
          </button>
        </FileItem>
      ))}
    </Container>
  );
};

export const FileField = memo(FileFieldComponent);
